import React, { useState, useEffect, useMemo, useRef } from 'react';
import logger from 'utils/logger';
import { Link, useLocation } from 'react-router-dom';
import { signOutWithTimeout } from '../../utils/authUtils';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';
import './styles/NavBar.css';

import Notifications from '../common/Notifications';
import { normalizeLocationsClaims, fetchMergedUserLocations, userHasLocationAccess } from '../../utils/roleUtils';
import { hasMhopClaim } from '../../utils/mhopCompat';
import { guestPermissionsFromClaims, getGuestHomePath } from '../../utils/guestAuth';
import { clearLogoutReason, setRequireCredentialReauth } from '../../utils/authSession';
import { shouldHideAppNav } from '../../utils/publicRoutes';
import {
  PAYCOR_SIGNIN_URL,
  FINANCE_NAV_FORMS,
  financeFormPdfUrl
} from '../../constants/financeForms';
import { hasReceiptsAccessFromContextUser } from '../../utils/receiptsAccess';
import { hasContractsAccessFromContextUser } from '../../utils/contractsAccess';
import { isThemePreviewOn, setThemePreview } from '../../utils/themePreview';
import { canViewClientPhi } from '../../utils/developmentPhi';

/** Derive nav role flags from AuthContext (same source as ProtectedRoute / page gates). */
function rolesFromUser(user) {
  if (!user) {
    return {
      isAdmin: false,
      isStaff: false,
      isManager: false,
      isDirector: false,
      isProgramManager: false,
      isCompliance: false,
      isExecutive: false,
      isHR: false,
      isOperations: false,
      isDriver: false,
      isRelief: false,
      isReliefManager: false,
      isDevelopment: false,
      behavioralHealth: false,
      mhop: false,
      isCaseManagerStaff: false,
      isGuest: false,
      guestPermissions: {
        clientProfile: false,
        addClient: false,
        programs: false
      },
      locations: []
    };
  }
  const claims = user.claims || {};
  const isAdmin = user.isAdmin === true || claims.admin === true || claims.role === 'admin';
  const isGuest = user.isGuest === true || claims.guest === true;
  return {
    isAdmin,
    isStaff: !isGuest && (claims.staff === true || isAdmin),
    isManager: !isGuest && (claims.manager === true || claims.director === true),
    isDirector: !isGuest && claims.director === true,
    isProgramManager: !isGuest && (
      claims.programManager === true ||
      claims.role === 'programManager' ||
      user.isProgramManager === true
    ),
    isCompliance: !isGuest && claims.compliance === true,
    isExecutive: !isGuest && claims.executive === true,
    isHR: !isGuest && claims.hr === true,
    isOperations: !isGuest && (claims.operations === true || claims.role === 'operations'),
    isDriver: !isGuest && claims.driver === true,
    isRelief: !isGuest && (claims.relief === true || user.isRelief === true),
    isReliefManager: !isGuest && (claims.reliefManager === true || user.isReliefManager === true),
    isDevelopment: !isGuest && (claims.development === true || user.isDevelopment === true),
    mhop: !isGuest && hasMhopClaim(claims),
    behavioralHealth: !isGuest && hasMhopClaim(claims),
    isCaseManagerStaff: !isGuest && (
      claims.caseManagerStaff === true ||
      user.isCaseManagerStaff === true
    ),
    isGuest,
    guestPermissions: user.guestPermissions || guestPermissionsFromClaims(claims),
    locations: normalizeLocationsClaims(claims.locations || user.locations || [])
  };
}

const DESKTOP_NAV_MEDIA = '(min-width: 992px)';
const HOVER_DROPDOWN_MEDIA = '(hover: hover) and (pointer: fine)';
const TOUCH_PRIMARY_MEDIA = '(pointer: coarse)';

const PROGRAM_NAV_LOCATIONS = ['Drop-In', 'Coronado', 'Eddy', 'ETH', 'Women\'s Flex', 'AWP', 'MRSC', 'WRC'];

const PROGRAM_NAV_LINKS = {
  'Drop-In': { to: '/drop-in', label: 'Drop-In Center', icon: 'fa-door-open' },
  Coronado: { to: '/coronado', label: 'Coronado Hotel', icon: 'fa-hotel' },
  Eddy: { to: '/eddy', label: 'Eddy St', icon: 'fa-building' },
  ETH: { to: '/eth', label: 'ETH', icon: 'fa-home' },
  'Women\'s Flex': { to: '/womens-flex', label: 'Women\'s Flex', icon: 'fa-users' },
  AWP: { to: '/awp', label: 'A Woman\'s Place', icon: 'fa-bed' },
  MRSC: { to: '/mrsc', label: 'Medical Respite & Sobering Center', icon: 'fa-hospital' },
  WRC: { to: '/wrc', label: 'WRC', icon: 'fa-hands-helping' }
};

function programNavKey(location) {
  if (location === 'AWP WRC') {
    return 'WRC';
  }
  return location;
}

function useNavLayout() {
  const [layout, setLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return { isWideNav: false, useHoverDropdowns: false };
    }
    const isWideNav = window.matchMedia(DESKTOP_NAV_MEDIA).matches;
    const isTouchPrimary = window.matchMedia(TOUCH_PRIMARY_MEDIA).matches;
    const canHover = window.matchMedia(HOVER_DROPDOWN_MEDIA).matches;
    return {
      isWideNav,
      useHoverDropdowns: isWideNav && canHover && !isTouchPrimary
    };
  });

  useEffect(() => {
    const wideQuery = window.matchMedia(DESKTOP_NAV_MEDIA);
    const hoverQuery = window.matchMedia(HOVER_DROPDOWN_MEDIA);
    const touchQuery = window.matchMedia(TOUCH_PRIMARY_MEDIA);

    const syncLayout = () => {
      setLayout({
        isWideNav: wideQuery.matches,
        useHoverDropdowns: wideQuery.matches && hoverQuery.matches && !touchQuery.matches
      });
    };

    syncLayout();
    wideQuery.addEventListener('change', syncLayout);
    hoverQuery.addEventListener('change', syncLayout);
    touchQuery.addEventListener('change', syncLayout);

    return () => {
      wideQuery.removeEventListener('change', syncLayout);
      hoverQuery.removeEventListener('change', syncLayout);
      touchQuery.removeEventListener('change', syncLayout);
    };
  }, []);

  return layout;
}

const CustomNavbar = () => {

  const { currentUser, markIntentionalLogout } = useAuth();
  const location = useLocation();
  const { isWideNav, useHoverDropdowns } = useNavLayout();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownCloseTimerRef = useRef(null);
  const userRoles = useMemo(() => rolesFromUser(currentUser), [currentUser]);
  const canShowReceipts = useMemo(
    () => hasReceiptsAccessFromContextUser(currentUser),
    [currentUser]
  );
  const canShowContracts = useMemo(
    () => hasContractsAccessFromContextUser(currentUser),
    [currentUser]
  );
  const [mergedNavLocations, setMergedNavLocations] = useState([]);
  const [themePreviewOn, setThemePreviewOn] = useState(() => isThemePreviewOn());

  const handleThemePreviewToggle = (event) => {
    event.stopPropagation();
    const next = event.target.checked;
    setThemePreview(next);
    setThemePreviewOn(next);
  };

  useEffect(() => {
    if (!currentUser) {
      setMergedNavLocations([]);
      return undefined;
    }

    const fallback = normalizeLocationsClaims(
      currentUser.claims?.locations || currentUser.locations || []
    );
    setMergedNavLocations(fallback);

    let cancelled = false;
    fetchMergedUserLocations(currentUser)
      .then((locations) => {
        if (!cancelled) {
          setMergedNavLocations(locations);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMergedNavLocations(fallback);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const showClientPhi = canViewClientPhi(currentUser);
  const canAccessProgram = (location) => showClientPhi && (
    userHasLocationAccess(mergedNavLocations, location) ||
    userRoles?.isAdmin ||
    userRoles?.isExecutive ||
    (userRoles?.isGuest && userRoles?.guestPermissions?.programs && userHasLocationAccess(mergedNavLocations, location))
  );
  const guestHomePath = userRoles.isGuest ? getGuestHomePath(currentUser?.claims || {}) : '/';
  const canViewReporting =
    userRoles.isAdmin ||
    userRoles.isCompliance ||
    userRoles.isExecutive ||
    userRoles.isManager ||
    userRoles.isDirector ||
    userRoles.isDevelopment;
  const canViewProgramNotes =
    userRoles.isAdmin ||
    userRoles.isManager ||
    userRoles.isDirector ||
    userRoles.isProgramManager ||
    userRoles.isCompliance ||
    userRoles.isCaseManagerStaff;

  const guestAssignedPrograms = useMemo(() => {
    if (!userRoles.isGuest || !userRoles.guestPermissions?.programs) {
      return [];
    }
    const seen = new Set();
    const links = [];
    mergedNavLocations.forEach((location) => {
      const key = programNavKey(location);
      if (!PROGRAM_NAV_LOCATIONS.includes(key) || seen.has(key)) {
        return;
      }
      if (!userHasLocationAccess(mergedNavLocations, key)) {
        return;
      }
      seen.add(key);
      links.push({ location: key, ...PROGRAM_NAV_LINKS[key] });
    });
    return links;
  }, [userRoles.isGuest, userRoles.guestPermissions?.programs, mergedNavLocations]);

  const guestShowsProgramsDropdown = userRoles.isGuest && (
    userRoles.guestPermissions?.addClient ||
    userRoles.guestPermissions?.clientProfile ||
    guestAssignedPrograms.length > 1
  );

  const guestShowsSingleProgramLink = userRoles.isGuest &&
    userRoles.guestPermissions?.programs &&
    guestAssignedPrograms.length === 1 &&
    !userRoles.guestPermissions?.addClient &&
    !userRoles.guestPermissions?.clientProfile;

  const hasAccess = (requiredLocations) => {
    if (userRoles.isAdmin || userRoles.isDevelopment) {
      return true;
    }
    const hasLocation = requiredLocations.some((loc) => userHasLocationAccess(mergedNavLocations, loc));
    if (userRoles.isGuest) {
      return userRoles.guestPermissions?.programs && hasLocation;
    }
    return hasLocation;
  };

  const handleDropdownOpen = (menuId) => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    setOpenDropdownId(menuId);
  };

  const handleDropdownClose = () => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
    }
    dropdownCloseTimerRef.current = setTimeout(() => {
      setOpenDropdownId(null);
      dropdownCloseTimerRef.current = null;
    }, 150);
  };

  const toggleDropdown = menuId => {
    setOpenDropdownId(prev => (prev === menuId ? null : menuId));
  };

  const dropdownNavProps = (menuId, extraClass = '') => {
    const className = `navbar-item${extraClass ? ` ${extraClass}` : ''}${openDropdownId === menuId ? ' menu-open' : ''}`;

    if (!useHoverDropdowns) {
      return { className };
    }

    return {
      className,
      onMouseEnter: () => handleDropdownOpen(menuId),
      onMouseLeave: handleDropdownClose
    };
  };

  const dropdownTriggerProps = menuId => {
    const isOpen = openDropdownId === menuId;

    return {
      role: 'button',
      tabIndex: 0,
      'aria-haspopup': 'true',
      'aria-expanded': isOpen,
      onClick: e => {
        e.preventDefault();
        e.stopPropagation();
        toggleDropdown(menuId);
      },
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleDropdown(menuId);
        }
      }
    };
  };

  // Cleanup effect for mobile menu
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      if (dropdownCloseTimerRef.current) {
        clearTimeout(dropdownCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!openDropdownId) {
      return undefined;
    }

    const closeOnOutsideTap = event => {
      if (event.target.closest('.navbar-item.menu-open')) {
        return;
      }
      setOpenDropdownId(null);
    };

    document.addEventListener('click', closeOnOutsideTap, true);
    return () => document.removeEventListener('click', closeOnOutsideTap, true);
  }, [openDropdownId]);

  if (shouldHideAppNav(location.pathname) || !currentUser || !auth.currentUser) {
    return null;
  }

  const handleLogout = async () => {
    clearLogoutReason();
    setRequireCredentialReauth();
    markIntentionalLogout();
    logger.debug('NavBar - Initiating logout');
    try {
      await signOutWithTimeout();
      logger.debug('NavBar - Logout successful, redirecting to login');
    } catch (error) {
      logger.error('Error signing out:', error);
    } finally {
      window.location.replace('/login');
    }
  };

  // Helper to close mobile menu and reset scroll
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdownId(null);
    document.body.style.overflow = '';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((open) => {
      if (!open) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return !open;
    });
  };

  return (
    <>
      {!isWideNav && isMobileMenuOpen && (
        <button
          type="button"
          className="navbar-menu-backdrop"
          aria-label="Close menu"
          onClick={closeMobileMenu}
        />
      )}

      <nav className={`cfsf-navbar${isWideNav ? ' cfsf-navbar--wide' : ' cfsf-navbar--drawer'}`}>
        <div className="navbar-container">
          <Link to={guestHomePath} className="navbar-brand">
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="Community Forward SF Logo"
              className="navbar-logo"
              width="200"
              height="40" />

          </Link>

          <button
            type="button"
            className={`mobile-menu-button ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}>

            <i className={`fas fa-${isMobileMenuOpen ? 'times' : 'bars'}`}></i>
          </button>

          <div className={`navbar-menu ${isMobileMenuOpen ? 'active' : ''}`}>
            {!isWideNav && (
              <div className="navbar-menu-header">
                <span className="navbar-menu-title">Menu</span>
                <button
                  type="button"
                  className="navbar-menu-close"
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
            )}

            {!userRoles.isGuest &&
            <Link to={guestHomePath} className="navbar-link" onClick={closeMobileMenu}>
              <i className="fas fa-home"></i>
              Home
            </Link>
            }

            {!userRoles.isGuest &&
            <div {...dropdownNavProps('apps')}>
              <div className="navbar-link navbar-link--dropdown" {...dropdownTriggerProps('apps')}>
                <span className="navbar-link__label">
                  <i className="fas fa-th-large"></i>
                  Apps
                </span>
                {!useHoverDropdowns && (
                  <i className={`fas fa-chevron-${openDropdownId === 'apps' ? 'up' : 'down'} navbar-link__chevron`} aria-hidden="true" />
                )}
              </div>
              <div className="navbar-dropdown">
                <a
                  href="https://www.gmail.com"
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-envelope"></i>
                  Gmail
                </a>
                <a
                  href={PAYCOR_SIGNIN_URL}
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-money-bill-wave"></i>
                  Paycor
                </a>
                <a
                  href="http://time.paycor.com/kiosk/115996"
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-clock"></i>
                  Clock In
                </a>
                <a
                  href="https://drive.google.com"
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-hdd"></i>
                  Google Drive
                </a>
                <a
                  href="https://communityforwardsf.allvoices.co/"
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-comments"></i>
                  All Voices
                </a>
                {(userRoles.isManager || userRoles.isAdmin || userRoles.isExecutive || userRoles.isDevelopment) &&
                <a
                  href="https://app.asana.com"
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-tasks"></i>
                    Asana
                </a>
                }
              </div>
            </div>
            }

            {guestShowsSingleProgramLink && guestAssignedPrograms[0] &&
            <Link
              to={guestAssignedPrograms[0].to}
              className="navbar-link"
              onClick={closeMobileMenu}
            >
              <i className={`fas ${guestAssignedPrograms[0].icon}`}></i>
              {guestAssignedPrograms[0].label}
            </Link>
            }

            {guestShowsSingleProgramLink &&
            <button
              type="button"
              className="navbar-link navbar-link--button"
              onClick={() => {
                closeMobileMenu();
                handleLogout();
              }}
            >
              <i className="fas fa-user-friends"></i>
              Switch user
            </button>
            }

            {/* Programs Dropdown */}
            {(() => {
              const shouldShowPrograms = userRoles.isGuest
                ? guestShowsProgramsDropdown
                : (
                  (showClientPhi && (
                    userRoles?.locations?.includes('Drop-In') ||
                    userRoles?.locations?.includes('Coronado') ||
                    userRoles?.locations?.includes('Eddy') ||
                    userRoles?.locations?.includes('ETH') ||
                    userRoles?.locations?.includes('Women\'s Flex') ||
                    userRoles?.locations?.includes('WRC') ||
                    userRoles?.isAdmin ||
                    userRoles?.isStaff ||
                    userRoles?.isExecutive ||
                    userRoles?.isManager ||
                    userRoles?.isDirector ||
                    userRoles?.isProgramManager ||
                    canViewProgramNotes
                  )) ||
                  userRoles?.isRelief ||
                  userRoles?.isReliefManager
                );

              // Debug logging for Programs dropdown visibility
              logger.debug('Programs dropdown visibility check:', {
                shouldShow: shouldShowPrograms,
                userRoles,
                hasDropIn: userRoles?.locations?.includes('Drop-In'),
                hasCoronado: userRoles?.locations?.includes('Coronado'),
                hasEddy: userRoles?.locations?.includes('Eddy'),
                isAdmin: userRoles?.isAdmin,
                isStaff: userRoles?.isStaff
              });

              return shouldShowPrograms;
            })() &&
            <div {...dropdownNavProps('programs')}>
              <div className="navbar-link navbar-link--dropdown" {...dropdownTriggerProps('programs')}>
                <span className="navbar-link__label">
                  <i className="fas fa-building"></i>
                  Programs
                </span>
                {!useHoverDropdowns && (
                  <i className={`fas fa-chevron-${openDropdownId === 'programs' ? 'up' : 'down'} navbar-link__chevron`} aria-hidden="true" />
                )}
              </div>
              <div className="navbar-dropdown navbar-dropdown--fit">
                {/* Client Management Section - Visible to admins, managers, staff, and executives */}
                {showClientPhi && (userRoles.isAdmin || userRoles.isManager || userRoles.isStaff || userRoles.isExecutive || canViewProgramNotes ||
                  (userRoles.isGuest && (userRoles.guestPermissions?.addClient || userRoles.guestPermissions?.clientProfile))) &&
                <div className="dropdown-section">
                  <div className="dropdown-section-title">Client Management</div>
                  {(userRoles.isAdmin || userRoles.isManager || userRoles.isStaff || userRoles.isExecutive || userRoles.guestPermissions?.addClient) &&
                  <Link to="/add-client" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-user-plus"></i>
                        Add New Client
                  </Link>
                  }
                  {(userRoles.isAdmin || userRoles.isManager || userRoles.isStaff || userRoles.isExecutive || userRoles.guestPermissions?.clientProfile) &&
                  <Link to="/client-list" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-list"></i>
                        Client Profiles
                  </Link>
                  }
                  {canViewProgramNotes &&
                  <Link to="/program-notes" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-user-shield"></i>
                        Program Notes
                  </Link>
                  }
                </div>
                }

                {showClientPhi && (userRoles.isAdmin || userRoles.isManager || userRoles.isStaff || userRoles.isExecutive || canViewProgramNotes ||
                  (userRoles.isGuest && (userRoles.guestPermissions?.addClient || userRoles.guestPermissions?.clientProfile))) &&
                <div className="dropdown-divider"></div>
                }

                {/* Programs Section */}
                <div className="dropdown-section">
                  <div className="dropdown-section-title">Programs</div>
                  {canAccessProgram('Drop-In') &&
                  <Link to="/drop-in" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-door-open"></i>
                        Drop-In Center
                  </Link>
                  }
                  {canAccessProgram('Coronado') &&
                  <Link to="/coronado" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-hotel"></i>
                        Coronado Hotel
                  </Link>
                  }
                  {canAccessProgram('Eddy') &&
                  <Link to="/eddy" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-building"></i>
                        Eddy St
                  </Link>
                  }
                  {canAccessProgram('ETH') &&
                  <Link to="/eth" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-home"></i>
                        ETH
                  </Link>
                  }
                  {canAccessProgram('Women\'s Flex') &&
                  <Link to="/womens-flex" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-users"></i>
                        Women's Flex
                  </Link>
                  }
                  {canAccessProgram('AWP') &&
                  <Link to="/awp" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-bed"></i>A Woman's Place
                  </Link>
                  }
                  {canAccessProgram('MRSC') &&
                  <Link to="/mrsc" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-hospital"></i>
                        Medical Respite & Sobering Center
                  </Link>
                  }
                  {canAccessProgram('WRC') &&
                  <Link to="/wrc" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-hands-helping"></i>
                        WRC
                  </Link>
                  }
                  {(() => {
                    const shouldShowMhop =
                    showClientPhi && (userRoles?.mhop || userRoles?.isAdmin);

                    logger.debug('MHOP link visibility check:', {
                      shouldShow: shouldShowMhop,
                      mhop: userRoles?.mhop,
                      isAdmin: userRoles?.isAdmin,
                      isManager: userRoles?.isManager
                    });

                    return shouldShowMhop;
                  })() &&
                  <Link
                    to="/mhop"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-brain"></i>
                        MHOP
                  </Link>
                  }
                  {(userRoles.isAdmin || userRoles.isRelief || userRoles.isReliefManager) &&
                  <Link
                    to="/relief-pool"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-people-arrows"></i>
                        Relief Pool
                  </Link>
                  }
                </div>
              </div>
            </div>
            }

            {(userRoles.isStaff || userRoles.isAdmin || userRoles.isManager || userRoles.isCompliance || userRoles.isHR || userRoles.isExecutive || userRoles.isOperations || userRoles.isDevelopment) &&
            <div {...dropdownNavProps('operations-finance', 'navbar-item--dropdown-end')}>
              <div className="navbar-link navbar-link--dropdown" {...dropdownTriggerProps('operations-finance')}>
                <span className="navbar-link__label">
                  <i className="fas fa-cogs"></i>
                  Operations + Finance
                </span>
                {!useHoverDropdowns && (
                  <i className={`fas fa-chevron-${openDropdownId === 'operations-finance' ? 'up' : 'down'} navbar-link__chevron`} aria-hidden="true" />
                )}
              </div>
              <div className="navbar-dropdown">
                <div className="dropdown-section">
                  <div className="dropdown-section-title">Operations</div>
                  {showClientPhi &&
                  <Link
                    to="/incident-reports"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-exclamation-triangle"></i>
                      Incident Reports
                  </Link>
                  }
                  <Link
                    to="/facilities-requests"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-tools"></i>
                      Facilities Requests
                  </Link>
                  {canShowContracts &&
                  <Link to="/contracts" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-file-contract"></i>
                      Contracts
                  </Link>
                  }
                  {hasAccess(['Coronado', 'Eddy']) &&
                  <Link
                    to="/pest-control-form"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-bug"></i>
                        Pest Control
                  </Link>
                  }
                  {(userRoles.isManager || userRoles.isAdmin || userRoles.isExecutive || userRoles.isDevelopment) &&
                  <Link
                    to="/purchasing-requests"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-shopping-cart"></i>
                        Purchasing Requests
                  </Link>
                  }
                  {(userRoles.isDriver || userRoles.isManager || userRoles.isAdmin || userRoles.isExecutive || userRoles.isDevelopment) &&
                  <>
                    <Link
                      to="/daily-vehicle-checklist"
                      className="dropdown-item"
                      onClick={closeMobileMenu}>

                      <i className="fas fa-car"></i>
                          Daily Vehicle Checklist
                    </Link>
                    <Link
                      to="/driver-inspection-end-of-shift"
                      className="dropdown-item"
                      onClick={closeMobileMenu}>

                      <i className="fas fa-clipboard-check"></i>
                          Driver Inspection End of Shift
                    </Link>
                  </>
                  }
                </div>
                <div className="dropdown-divider"></div>
                <div className="dropdown-section">
                  <div className="dropdown-section-title">Finance</div>
                  <div className="dropdown-submenu">
                    <div className="dropdown-item dropdown-submenu-trigger" tabIndex={0}>
                      <i className="fas fa-folder-open"></i>
                      Finance Forms
                      <i className="fas fa-chevron-right dropdown-submenu-chevron" aria-hidden="true" />
                    </div>
                    <div className="dropdown-submenu-panel">
                      <Link
                        to="/reimbursement-form"
                        className="dropdown-item"
                        onClick={closeMobileMenu}>

                        <i className="fas fa-file-invoice-dollar"></i>
                        Reimbursement Requests
                      </Link>
                      <Link
                        to="/cellphone-stipend-form"
                        className="dropdown-item"
                        onClick={closeMobileMenu}>

                        <i className="fas fa-mobile-alt"></i>
                        Cellphone Stipend Agreement
                      </Link>
                      {FINANCE_NAV_FORMS.map((form) =>
                        <a
                          key={form.file}
                          href={financeFormPdfUrl(form.file)}
                          className="dropdown-item"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMobileMenu}>

                          <i className={form.icon}></i>
                          {form.name}
                        </a>
                      )}
                      {canShowReceipts &&
                        <Link
                          to="/receipts"
                          className="dropdown-item"
                          onClick={closeMobileMenu}>

                          <i className="fas fa-receipt"></i>
                          Receipts
                        </Link>
                      }
                    </div>
                  </div>
                  <Link
                    to="/vendor-forms"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-file-alt"></i>
                      Vendor Forms
                  </Link>
                  <a
                    href={PAYCOR_SIGNIN_URL}
                    className="dropdown-item"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-money-bill-wave"></i>
                      Paycor
                  </a>
                </div>
              </div>
            </div>
            }

            {(userRoles.isStaff ||
            userRoles.isAdmin ||
            userRoles.isCompliance ||
            userRoles.isExecutive ||
            userRoles.isHR ||
            userRoles.isDevelopment) &&
            <div {...dropdownNavProps('knowledge-base', 'navbar-item--dropdown-end')}>
              <div className="navbar-link navbar-link--dropdown" {...dropdownTriggerProps('knowledge-base')}>
                <span className="navbar-link__label">
                  <i className="fas fa-book"></i>
                  Knowledge Base
                </span>
                {!useHoverDropdowns && (
                  <i className={`fas fa-chevron-${openDropdownId === 'knowledge-base' ? 'up' : 'down'} navbar-link__chevron`} aria-hidden="true" />
                )}
              </div>
              <div className="navbar-dropdown">
                <a
                  href="https://docs.google.com/document/d/1rSQ8dXHa2Axo9lTQQ8Bm11lp-IIiMm-Ahq3219GILbc/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dropdown-item"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-book"></i>
                    Operations Manual
                </a>
                <a
                  href="https://my.nicheacademy.com/communityforwardsf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dropdown-item"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-laptop"></i>
                    Niche Academy
                </a>
                {(!userRoles.isExecutive || userRoles.isAdmin || userRoles.isHR || userRoles.isDevelopment) &&
                <Link
                  to="/trainings"
                  className="dropdown-item"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-graduation-cap"></i>
                    Trainings
                </Link>
                }
                <Link
                  to="/employee-reviews"
                  className="dropdown-item"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-clipboard-list"></i>
                    My reviews
                </Link>
                <Link to="/policies" className="dropdown-item" onClick={closeMobileMenu}>
                  <i className="fas fa-file-contract"></i>
                    Policies, Guidelines, and Procedures
                </Link>
              </div>
            </div>
            }

            {/* Reports Dropdown - leadership and compliance */}
            {canViewReporting &&
            <div {...dropdownNavProps('reporting', 'navbar-item--dropdown-end')}>
              <div className="navbar-link navbar-link--dropdown" {...dropdownTriggerProps('reporting')}>
                <span className="navbar-link__label">
                  <i className="fas fa-chart-bar"></i>
                  Reporting
                </span>
                {!useHoverDropdowns && (
                  <i className={`fas fa-chevron-${openDropdownId === 'reporting' ? 'up' : 'down'} navbar-link__chevron`} aria-hidden="true" />
                )}
              </div>
              <div className="navbar-dropdown">
                <div className="dropdown-section">
                  <div className="dropdown-section-title">Analytics</div>
                  <Link to="/drop-in/reports" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-chart-area"></i>
                      Drop-In Reports
                  </Link>
                  <Link to="/coronado/reports" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-hotel"></i>
                      Coronado Reports
                  </Link>
                  <Link to="/eddy/reports" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-building"></i>
                      Eddy Reports
                  </Link>
                  <Link to="/awp/reports" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-bed"></i>
                      A Woman&apos;s Place Reports
                  </Link>
                  <Link to="/wrc/reports" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-hands-helping"></i>
                      WRC Reports
                  </Link>
                  <Link to="/reports" className="dropdown-item" onClick={closeMobileMenu}>
                    <i className="fas fa-chart-line"></i>
                      Reports Dashboard
                  </Link>
                  {(!userRoles.isDevelopment || userRoles.isAdmin) && (
                    <Link
                      to="/tableau-dashboard"
                      className="dropdown-item"
                      onClick={closeMobileMenu}>

                      <i className="fas fa-chart-pie"></i>
                        Tableau Dashboard
                    </Link>
                  )}
                </div>
              </div>
            </div>
            }

            {/* Notifications */}
            {!userRoles.isGuest && <Notifications />}

            <div {...dropdownNavProps('user', 'navbar-item--dropdown-end')}>
              <div className="user-menu" {...dropdownTriggerProps('user')}>
                <div className="user-avatar">{currentUser.displayName?.charAt(0) || 'U'}</div>
                <span className="user-name">{currentUser.displayName || 'User'}</span>
                {userRoles.isAdmin && <span className="admin-badge">Admin</span>}
                {!useHoverDropdowns && (
                  <i className={`fas fa-chevron-${openDropdownId === 'user' ? 'up' : 'down'} navbar-link__chevron`} aria-hidden="true" />
                )}
              </div>
              <div className="navbar-dropdown">
                {userRoles.isAdmin &&
                <>
                  <Link
                    to="/admin-dashboard"
                    className="dropdown-item"
                    onClick={closeMobileMenu}
                  >
                    <i className="fas fa-user-shield"></i>
                    Admin Dashboard
                  </Link>
                  <Link
                    to="/admin-dashboard?tab=site"
                    className="dropdown-item"
                    onClick={closeMobileMenu}
                  >
                    <i className="fas fa-sliders-h"></i>
                    Site appearance
                  </Link>
                </>
                }
                {userRoles.isAdmin &&
                <Link
                  to="/admin/access-logs"
                  className="dropdown-item"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-eye"></i>
                    Access Logs
                </Link>
                }
                {(userRoles.isAdmin || userRoles.isHR) &&
                <>
                  <Link
                    to="/admin/trainings"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-graduation-cap"></i>
                      Trainings Admin
                  </Link>
                  <Link
                    to="/admin/employee-reviews"
                    className="dropdown-item"
                    onClick={closeMobileMenu}>

                    <i className="fas fa-clipboard-check"></i>
                      Employee reviews
                  </Link>
                </>
                }
                {userRoles.isAdmin &&
                <Link
                  to="/admin/email-notifications"
                  className="dropdown-item"
                  onClick={closeMobileMenu}>

                  <i className="fas fa-envelope"></i>
                    Email notifications
                </Link>
                }
                <div
                  className="dropdown-item navbar-theme-toggle"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                  role="presentation"
                >
                  <i className="fas fa-palette" aria-hidden="true"></i>
                  <label className="navbar-theme-toggle__label" htmlFor="navbar-theme-preview-toggle">
                    Try new theme
                  </label>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="navbar-theme-preview-toggle"
                      checked={themePreviewOn}
                      onChange={handleThemePreviewToggle}
                      aria-label="Try new theme for the entire site"
                    />
                  </div>
                </div>
                <div
                  className="dropdown-item"
                  onClick={() => {
                    handleLogout();
                    closeMobileMenu();
                  }}>

                  <i className="fas fa-sign-out-alt"></i>
                  {userRoles.isGuest ? 'Switch user' : 'Logout'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>);

};

export default CustomNavbar;
